import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getContractTypeAmount,
  getConstructionTypeAmount,
  getCompanyAmount,
  getAllContracts,
} from "../../api/contract/bill";
import NivoBar from "../../components/common/graphs/NivoBar";
import NivoPie from "../../components/common/graphs/NivoPie";
import NivoHorizontalBar from "../../components/common/graphs/NivoHorizontalBar";
import BoardTable from "../../components/common/board/BoardTable";
import BoardPagination from "../../components/common/board/BoardPagination";

const Bill = () => {
  const { id: projectId } = useParams();
  const [typeAmount, setTypeAmount] = useState([]);
  const [typeKeys, setTypeKeys] = useState([]);
  const [consAmount, setConsAmount] = useState([]);
  const [consKeys, setConsKeys] = useState([]);
  const [consCompany, setConsCompany] = useState([]);
  const [subCompany, setSubCompany] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [selectedContracts, setSelectedContracts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    async function fetchData() {
      try {
        const [typeRes, consRes, compRes, allRes] = await Promise.all([
          getContractTypeAmount(projectId),
          getConstructionTypeAmount(projectId),
          getCompanyAmount(projectId),
          getAllContracts(projectId, page, pageSize),
        ]);

        // 계약 구분별
        const typeKeys = typeRes.map((s) => s.id);
        setTypeKeys(typeKeys);
        const mergedType = {};
        typeRes.forEach((series) => {
          series.data.forEach((point) => {
            const month = point.x;
            if (!mergedType[month]) {
              mergedType[month] = { month };
              typeKeys.forEach((k) => (mergedType[month][k] = 0));
            }
            mergedType[month][series.id] = point.y ?? 0;
          });
        });
        setTypeAmount(
          Object.values(mergedType).sort(
            (a, b) => new Date(a.month) - new Date(b.month)
          )
        );

        // 공종별
        const consKeys =
          consRes.length > 0
            ? Object.keys(consRes[0]).filter((k) => k !== "month")
            : [];
        setConsKeys(consKeys);
        setConsAmount(consRes);

        // 회사별
        setConsCompany(
          (compRes["시공"] || []).map((c) => ({
            id: c.company,
            value: c.value,
          }))
        );
        setSubCompany(
          (compRes["하도급"] || []).map((s) => ({
            id: s.company,
            value: s.value,
          }))
        );

        // 전체 계약
        setContracts(allRes || []);
        setSelectedContracts(allRes.slice(0, 5) || []);
        setTotal(allRes.count || 0);
      } catch (err) {
        console.error("비용 데이터 불러오기 실패:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [projectId, page]);

  if (loading) return <p>Loading...</p>;

  // 선택된 계약만 막대그래프에 반영
  const barData = selectedContracts
    .sort((a, b) => a.amount - b.amount) // 금액순 오름차순
    .map((c) => ({
      id: c.id,
      name: `${c.title}`,
      amount: c.amount,
    }));

  const columns = [
    { header: "공종", key: "construction_type" },
    { header: "계약 구분", key: "contract_type" },
    { header: "계약명", key: "title" },
    { header: "회사명", key: "company" },
    {
      header: "금액",
      key: "amount",
      render: (row) =>
        row.amount ? `${Number(row.amount).toLocaleString("ko-KR")}원` : "-",
    },
  ];

  // 행 클릭 → 선택/해제
  const handleRowClick = (row) => {
    setSelectedContracts((prev) => {
      const exists = prev.find((c) => c.id === row.id);
      if (exists) {
        return prev.filter((c) => c.id !== row.id); // 해제
      } else {
        return [...prev, row]; // 추가
      }
    });
  };
  const totalAmount = contracts.reduce((sum, c) => sum + (c.amount || 0), 0);
  console.log(consAmount);
  return (
    <div className="max-w-full mx-auto p-6 space-y-10 bg-gray-50">
      <h2 className="text-2xl font-bold mb-6">지출/청구 관리</h2>
      <h3 className="text-2xl font-bold mb-6">
        계약 총 사용금액: {Number(totalAmount).toLocaleString("ko-KR")}원
      </h3>

      <div className="overflow-x-auto">
        <div className="flex gap-6 min-w-[1200px]">
          {/* 왼쪽: 그래프 묶음 */}
          <div className="flex-1 flex flex-col gap-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <NivoBar
                data={typeAmount}
                keys={typeKeys}
                indexBy="month"
                title="월별 계약 금액 (계약 구분별)"
                groupMode="stacked"
                height={300}
              />
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <NivoBar
                data={consAmount}
                keys={consKeys}
                indexBy="month"
                title="월별 계약 금액 (공종별)"
                groupMode="stacked"
                height={300}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <NivoPie
                  data={consCompany}
                  title="시공 계약 회사별 금액"
                  donut
                />
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <NivoPie
                  data={subCompany}
                  title="하도급 계약 회사별 금액"
                  donut
                />
              </div>
            </div>
          </div>

          {/* 오른쪽: 선택된 계약 막대그래프 + 테이블 */}
          <div className="flex-1 flex flex-col bg-white p-4 rounded-lg shadow">
            {barData.length > 0 && (
              <div className="mb-6">
                <NivoHorizontalBar
                  data={barData}
                  keys={["amount"]}
                  indexBy="name"
                  height={500}
                  title="계약 금액 비교"
                />
              </div>
            )}

            <BoardTable
              columns={columns}
              rows={contracts}
              loading={loading}
              error={null}
              emptyText="계약이 없습니다."
              onRowClick={handleRowClick}
            />

            <div className="mt-4">
              <BoardPagination
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={setPage}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Bill;
